import type { TransactionSnapshot } from '@/domain';
import {
  RecoveryState,
  type ConnectorId,
  type StationId,
  type TransactionId,
} from '@/domain/types';
import type { TransactionRepository } from '@/persistence/ports';
import type { Evse3Database } from '../db';

/**
 * The persisted row is `TransactionSnapshot + stationId`. To get a useful
 * index on "open transactions" we materialise `stoppedAt` as a number (sentinel
 * `0` for "still open") because Dexie's `equals(undefined)` doesn't index.
 */
const OPEN_SENTINEL = 0;

export class TransactionRepositoryDexie implements TransactionRepository {
  constructor(private readonly db: Evse3Database) {}

  async byStation(stationId: StationId): Promise<readonly TransactionSnapshot[]> {
    const rows = await this.db.transactions.where('stationId').equals(stationId).toArray();
    return rows.map(stripStation);
  }

  async byConnector(
    stationId: StationId,
    connectorId: ConnectorId,
  ): Promise<readonly TransactionSnapshot[]> {
    const rows = await this.db.transactions
      .where('[stationId+connectorId]')
      .equals([stationId, connectorId])
      .toArray();
    return rows.map(stripStation);
  }

  async findOpen(stationId: StationId): Promise<readonly TransactionSnapshot[]> {
    // BUG-011 — also filter by recoveryState. The `[stationId+stoppedAt]`
    // index catches "never stopped" rows, but Resume / Clear-stale flip
    // `recoveryState` to Adopted / Orphaned WITHOUT setting `stoppedAt`
    // (that would be lying — Adopted will get a real StopTransaction next;
    // Orphaned never gets one, it's audit-only). So without this extra
    // filter, resolved rows kept coming back on every page load and the
    // user perceived the buttons as no-ops.
    //
    // Legacy rows (schema v1) didn't have `recoveryState` at all; coerce
    // to Live so they still surface in the dialog.
    const rows = await this.db.transactions
      .where('[stationId+stoppedAt]')
      .equals([stationId, OPEN_SENTINEL])
      .and((row) => (row.recoveryState ?? RecoveryState.Live) === RecoveryState.Live)
      .toArray();
    return rows.map(stripStation);
  }

  async upsert(stationId: StationId, snap: TransactionSnapshot): Promise<void> {
    await this.db.transactions.put({
      stationId,
      ...snap,
      // index materialisation: numeric for findOpen()
      stoppedAt: snap.stoppedAt ?? OPEN_SENTINEL,
      recoveryState: snap.recoveryState ?? RecoveryState.Live,
    });
  }

  async delete(stationId: StationId, id: TransactionId): Promise<void> {
    await this.db.transactions.delete([stationId, id]);
  }
}

function stripStation(row: TransactionSnapshot & { stationId: StationId }): TransactionSnapshot {
  const { stationId: _stationId, ...rest } = row;
  // Reverse the OPEN_SENTINEL materialisation so domain consumers see the
  // `stoppedAt undefined` they expect for active transactions.
  if (rest.stoppedAt === OPEN_SENTINEL) {
    const { stoppedAt: _stoppedAt, ...withoutStopped } = rest;
    return withoutStopped;
  }
  return rest;
}
